import * as CryptoJS from 'crypto-js';

// Character used to join 3 login details into one text
const KEY_SEPARATOR = '|';
// Microsoft login keys start with this — we never encrypt those keys
export const MSAL_STORAGE_KEY_PREFIX = 'msal.';

// Secret password is stored as a text string in app memory
export type LocalStorageEncryptionKey = string;

// Any value that can be saved in localStorage (text, number, true/false, etc.)
export type LocalStorageWritableValue =
  | string
  | number
  | boolean
  | null
  | undefined;

/**
 * Key 1.0.1 — Join 3 login details into one text (step 1 of making secret password).
 *
 * Parameter homeAccountId  → comes from Microsoft login account (cloud user id)
 * Parameter localAccountId → comes from Microsoft login account (user id on this device)
 * Parameter clientId       → comes from environment.clientId (our app id in settings file)
 *
 * Returns joined text like "abc|def|ghi". This is NOT the password yet.
 * Called by: bootstrapLocalStorageEncryptionFromMsal, LocalStorageKeyService.initializeFromAccount
 */
export function buildLocalStorageKeyMaterial(
  homeAccountId: string,
  localAccountId: string,
  clientId: string
): string {
  // Join all 3 values with | between them
  return `${homeAccountId}${KEY_SEPARATOR}${localAccountId}${KEY_SEPARATOR}${clientId}`;
}

/**
 * Key 1.0.2 — Convert joined text into secret password (step 2 of making secret password).
 *
 * Parameter keyMaterial → comes from buildLocalStorageKeyMaterial (Key 1.0.1)
 *
 * Returns long password text like "a3f8b2c1d9e4...". Kept in app memory only, never in browser.
 * Called by: bootstrapLocalStorageEncryptionFromMsal, LocalStorageKeyService.initializeFromAccount
 * Next: setLocalStorageEncryptionKey saves this in memory (Key 1.0.3)
 */
export function deriveLocalStorageEncryptionKey(
  keyMaterial: string
): LocalStorageEncryptionKey {
  // SHA256 formula — same input always gives same password
  return CryptoJS.SHA256(keyMaterial).toString(CryptoJS.enc.Hex);
}

/**
 * Encryption 2.0.3 / Decryption 3.0.3 — Hide the localStorage key name.
 *
 * Parameter plainKey      → comes from your screen code, example "hawbId", "userId"
 * Parameter encryptionKey → comes from getLocalStorageEncryptionKey() in memory (Key 1.0.4)
 *
 * Returns hidden name like "8f3a2b1c9d4e5f6a..." (64 characters).
 * Browser stores hidden name instead of "hawbId" so nobody can read it in DevTools.
 * Same name + same password = same hidden name (needed to find data when reading).
 */
export function hashLocalStorageKeyName(
  plainKey: string,
  encryptionKey: LocalStorageEncryptionKey
): string {
  // HMAC formula hides the name using the secret password
  return CryptoJS.HmacSHA256(plainKey, encryptionKey).toString(CryptoJS.enc.Hex);
}

/**
 * Encryption 2.0.5 — Convert value to text before encrypting.
 *
 * Parameter value → comes from localStorage.setItem, can be number/text/true/false
 * Returns text string, example: 123 → "123"
 */
export function normalizeLocalStorageValue(value: LocalStorageWritableValue): string {
  return String(value);
}

/**
 * Encryption 2.0.4 — Encrypt (scramble) the localStorage value before saving.
 *
 * Parameter plainText     → normal value from screen code, example "12345"
 * Parameter encryptionKey → comes from getLocalStorageEncryptionKey() passed by parent function
 *
 * Returns scrambled text like "U2FsdGVkX1..." — this is what browser stores.
 * Called by: safeEncrypt → writeEncryptedStorageItem
 */
export function encryptLocalStorageValue(
  plainText: LocalStorageWritableValue,
  encryptionKey: LocalStorageEncryptionKey
): string {
  // Make sure value is text first
  const text = normalizeLocalStorageValue(plainText);
  // AES formula scrambles text using secret password
  return CryptoJS.AES.encrypt(text, encryptionKey).toString();
}

/**
 * Decryption 3.0.4 — Decrypt (unscramble) the localStorage value after reading.
 *
 * Parameter cipherText    → scrambled text from browser, example "U2FsdGVkX1..."
 * Parameter encryptionKey → same secret password that was used when saving
 *
 * Returns normal value like "12345", or null if password wrong or data damaged.
 * Called by: safeDecrypt → readEncryptedStorageItem
 */
export function decryptLocalStorageValue(
  cipherText: string,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  // AES reverse formula — unscramble using same password
  const bytes = CryptoJS.AES.decrypt(cipherText, encryptionKey);
  // Convert back to readable text
  const plain = bytes.toString(CryptoJS.enc.Utf8);
  // Return null if result is empty (decrypt failed)
  return plain || null;
}

/**
 * Check if key name belongs to Microsoft login (starts with "msal.").
 * Parameter key → key name from localStorage
 * Microsoft keys are not encrypted — only PacTrak app keys are encrypted.
 */
export function isMsalStorageKey(key: string): boolean {
  return key.startsWith(MSAL_STORAGE_KEY_PREFIX);
}

// Pattern: hidden key names are exactly 64 letters/numbers
const HASHED_STORAGE_KEY_PATTERN = /^[a-f0-9]{64}$/i;

/**
 * Check if key name is already hidden (64 character code).
 * Parameter key → key name found in browser storage
 */
export function isHashedStorageKeyName(key: string): boolean {
  return HASHED_STORAGE_KEY_PATTERN.test(key);
}

// All AES encrypted values start with this text
const ENCRYPTED_VALUE_PREFIX = 'U2FsdGVkX1';

/**
 * Check if value is already encrypted (starts with "U2FsdGVkX1").
 * Parameter value → value read from browser storage
 */
export function isEncryptedStorageValue(value: string): boolean {
  return value.startsWith(ENCRYPTED_VALUE_PREFIX);
}
